import { useCallback, useEffect, useRef } from "react";
import type { Dispatch, SetStateAction } from "react";

import { MessageRow, MessagesWithPagination } from "../../api/message.type";
import {
  getAdminUserMessages,
  getChat,
  getGroupChatMessages,
} from "../../api/message.api";

interface UseMessagePaginationOptions {
  isGroupChat: boolean;
  groupChatId?: string;
  isAdminChat: boolean;
  adminUserId?: string;
  hasMoreMessages: boolean;
  setHasMoreMessages: Dispatch<SetStateAction<boolean>>;
  currentPage: number;
  setCurrentPage: Dispatch<SetStateAction<number>>;
  setIsLoadingMore: Dispatch<SetStateAction<boolean>>;
  isLoading: boolean;
  isLoadingMore: boolean;
  setMessages: Dispatch<SetStateAction<MessageRow[]>>;
  onMessageFound?: (messageId: string, attempts: number) => void;
  messages: MessageRow[];
  resetScopeKey: string;
}

export function useMessagePagination({
  isGroupChat,
  groupChatId,
  isAdminChat,
  adminUserId,
  hasMoreMessages,
  setHasMoreMessages,
  currentPage,
  setCurrentPage,
  setIsLoadingMore,
  isLoading,
  isLoadingMore,
  setMessages,
  onMessageFound,
  messages,
  resetScopeKey,
}: UseMessagePaginationOptions) {
  const messagesRef = useRef(messages);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  useEffect(() => {
    setCurrentPage(1);
    setIsLoadingMore(false);
  }, [resetScopeKey, setCurrentPage, setIsLoadingMore]);

  const loadMoreMessages = useCallback(
    async (
      count: number = 50,
      targetMessageId?: string,
      attempt: number = 1
    ): Promise<MessagesWithPagination | null> => {
      if (!hasMoreMessages) {
        return null;
      }

      if (!isLoading && !isLoadingMore) {
        if (isGroupChat && !groupChatId) {
          console.warn("loadMoreMessages_missing_group_chat_id");
          return null;
        }
        if (isAdminChat && !adminUserId) {
          console.warn("loadMoreMessages_missing_admin_user_id");
          return null;
        }
        setIsLoadingMore(true);
        try {
          const nextPage = currentPage + 1;

          const moreMessages = isGroupChat
            ? await getGroupChatMessages(groupChatId!, nextPage, count)
            : isAdminChat
            ? await getAdminUserMessages(adminUserId!, nextPage, count)
            : await getChat(nextPage, count);

          const fetchedMessages = Array.isArray(moreMessages.messages)
            ? moreMessages.messages
            : [];

          if (fetchedMessages.length > 0) {
            setMessages((prev) => [...prev, ...fetchedMessages]);
            setCurrentPage(nextPage);
            const newHasMore = (moreMessages?.nextPage ?? null) !== null;
            setHasMoreMessages(newHasMore);

            if (targetMessageId) {
              const foundInNew = fetchedMessages.some(
                (msg) => msg.id === targetMessageId
              );

              if (foundInNew) {
                setTimeout(() => {
                  if (onMessageFound) {
                    console.log(
                      `📞 Triggering onMessageFound callback for ${targetMessageId}`
                    );
                    onMessageFound(targetMessageId, attempt);
                  }
                }, 200);
              } else {
                console.log(
                  `🔍 Target message ${targetMessageId} not found in this batch of ${fetchedMessages.length} messages`
                );
              }
            }

            return moreMessages;
          } else {
            setHasMoreMessages(false);
            return null;
          }
        } catch (error) {
          console.error("Failed to load more messages:", error);
          return null;
        } finally {
          setIsLoadingMore(false);
        }
      }
      return null;
    },
    [
      currentPage,
      groupChatId,
      adminUserId,
      hasMoreMessages,
      isGroupChat,
      isAdminChat,
      isLoading,
      isLoadingMore,
      onMessageFound,
      setCurrentPage,
      setHasMoreMessages,
      setIsLoadingMore,
      setMessages,
    ]
  );

  const loadMessagesUntilId = useCallback(
    async (messageId: string, initialCount?: number): Promise<void> => {
      if (!messageId || messageId === "undefined") {
        console.warn("loadMessagesUntilId_invalid_id", messageId);
        return;
      }

      const messageExists = messagesRef.current.some(
        (msg) => msg.id === messageId
      );
      if (messageExists) {
        return;
      }

      let attempts = 0;
      const maxAttempts = 10;
      let countOverride = initialCount;

      while (hasMoreMessages && !isLoadingMore && attempts < maxAttempts) {
        attempts++;
        console.log(
          `Loading messages attempt ${attempts} to find message ${messageId}. Total messages loaded: ${messages.length}`
        );

        const count = countOverride ?? attempts * 100;
        countOverride = undefined;
        const result = await loadMoreMessages(count, messageId, attempts);

        const foundInResult = Array.isArray(result?.messages)
          ? result!.messages.some((msg) => msg.id === messageId)
          : false;

        const foundInState = messagesRef.current.some(
          (msg) => msg.id === messageId
        );

        if (foundInResult || foundInState) {
          break;
        }
      }

      if (attempts >= maxAttempts) {
        console.log(
          `Could not find message ${messageId} after ${maxAttempts} attempts`
        );
      }
    },
    [hasMoreMessages, isLoadingMore, loadMoreMessages, messages]
  );

  return {
    loadMoreMessages,
    loadMessagesUntilId,
  } as const;
}
